import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { Pie, PieChart as RePieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { VIZ_EXPANDED } from '../../viz.styles';
import { PieChartBlock as PieChartBlockType } from '../../viz.types';
import VizBlockFrame from '../VizBlockFrame';
import VizTotal from '../VizTotal';
import { usePieChartBlock } from './usePieChartBlock';

interface PieChartBlockProps {
  block: PieChartBlockType;
}

const INNER_RADIUS = 30;
const OUTER_RADIUS = 66;
const CHART_SIZE = OUTER_RADIUS * 2;
const LEGEND_DOT_SIZE = 8;
const LEGEND_SHARE_WIDTH = 36;

const LEGEND_WIDTH = 380;

const PieChartBlock = ({ block }: PieChartBlockProps) => {
  const { formatters, chartTheme, slices, total } = usePieChartBlock(block);

  return (
    <VizBlockFrame title={block.title} subtitle={block.subtitle}>
      <Stack
        sx={{
          gap: 3,
          [VIZ_EXPANDED]: {
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={(theme) => ({
            gap: 2,
            flexShrink: 0,
            [theme.breakpoints.down('sm')]: { justifyContent: 'center' },
          })}
        >
          <Box sx={{ width: CHART_SIZE, height: CHART_SIZE, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={INNER_RADIUS}
                  outerRadius={OUTER_RADIUS}
                  paddingAngle={2}
                  cornerRadius={chartTheme.sliceCornerRadius}
                />
                <Tooltip
                  {...chartTheme.tooltipStyles}
                  formatter={(v) => formatters.formatFull(Number(v))}
                />
              </RePieChart>
            </ResponsiveContainer>
          </Box>

          <VizTotal value={formatters.formatCompact(total)} isStacked />
        </Stack>

        <Stack
          sx={{
            alignSelf: 'stretch',
            gap: 2,
            [VIZ_EXPANDED]: { flex: 1, minWidth: 0, maxWidth: LEGEND_WIDTH },
          }}
        >
          {slices.map((slice, index) => (
            <Stack key={index} direction="row" alignItems="center" sx={{ gap: 1 }}>
              <Box
                sx={{
                  width: LEGEND_DOT_SIZE,
                  height: LEGEND_DOT_SIZE,
                  borderRadius: chartTheme.dotRadius,
                  flexShrink: 0,
                  backgroundColor: slice.fill,
                }}
              />
              <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                {slice.name}
              </Typography>
              <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
                {formatters.formatCompact(slice.value)}
              </Typography>
              {slice.share !== null && (
                <Typography
                  variant="micro1"
                  color="text.tertiary"
                  align="right"
                  sx={{ width: LEGEND_SHARE_WIDTH, flexShrink: 0 }}
                >
                  {formatters.formatShare(slice.share)}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </VizBlockFrame>
  );
};

export default PieChartBlock;
